import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ImageCarousel } from "@/components/ImageCarousel";
import { fileUrl } from "@/lib/api";
import { CLP, formatDateNumeric } from "@/lib/format";
import { Star, ThumbsUp, Users, CalendarDays, CheckCircle2, Heart, Layers, Sparkles } from "lucide-react";

function MineBadge({ paid, saved }) {
  if (paid) return (
    <Badge className="absolute bottom-2 left-2 z-10 text-[10px] px-1.5 py-0.5 gap-0.5 bg-emerald-600 text-white hover:bg-emerald-600 shadow" data-testid="card-paid-badge">
      <CheckCircle2 className="w-3 h-3" />Reservado
    </Badge>
  );
  if (saved) return (
    <Badge className="absolute bottom-2 left-2 z-10 text-[10px] px-1.5 py-0.5 gap-0.5 bg-background/95 text-coral hover:bg-background/95 shadow" data-testid="card-saved-badge">
      <Heart className="w-3 h-3 fill-current" />En tu Lista de Deseos
    </Badge>
  );
  return null;
}

function HoverMosaic({ images, alt }) {
  const imgs = (images || []).slice(0, 3);
  if (imgs.length < 2) return null;
  const cell = "w-full h-full min-h-0 object-cover transition-transform duration-500 group-hover:scale-105";
  return (
    <div className="absolute inset-0 z-[5] grid grid-cols-2 grid-rows-2 gap-0.5 bg-background opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-testid="card-hover-mosaic">
      <img src={fileUrl(imgs[0])} alt={alt} className={`${cell} row-span-2`} />
      {imgs.length === 2 ? <img src={fileUrl(imgs[1])} alt="" className={`${cell} row-span-2`} /> : (
        <>
          <img src={fileUrl(imgs[1])} alt="" className={cell} />
          <img src={fileUrl(imgs[2])} alt="" className={cell} />
        </>
      )}
    </div>
  );
}

export function CompactWorkshopCard({ workshop, isInterest = false, paid = false, saved = false }) {
  const spots = workshop.spots_left ?? 0;
  const capacity = workshop.capacity ?? 0;
  const offer = workshop.offer;
  const isFree = workshop.offer_active && workshop.effective_price_clp === 0;
  const offerLabel = isFree ? "GRATIS" : offer ? (offer.discount_type === "fixed" ? "OFERTA" : `-${offer.discount_value}% OFF`) : "";
  return (
    <Link to={`/taller/${workshop.id}`} data-testid={`workshop-card-${workshop.id}`}>
      <Card className="group overflow-hidden rounded-xl border-border/70 hover:shadow-[0_10px_30px_-12px_rgba(121,68,228,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.02] h-full">
        <div className="relative aspect-[16/9] overflow-hidden bg-muted">
          <ImageCarousel images={workshop.images?.slice(0, 1)} alt={workshop.name} className="w-full h-full" />
          <HoverMosaic images={workshop.images} alt={workshop.name} />
          <MineBadge paid={paid} saved={saved} />
          {workshop.featured && (
            <Badge className="absolute bottom-2 right-2 z-10 text-[10px] px-1.5 py-0.5 gap-0.5 bg-amber-400 text-[#3d2b00] hover:bg-amber-400 shadow" data-testid="card-featured-badge">
              <Sparkles className="w-3 h-3" />Destacado
            </Badge>
          )}
          <Badge className="absolute top-2 left-2 z-10 text-[10px] px-1.5 py-0.5 bg-background/90 text-foreground hover:bg-background/90 backdrop-blur">
            {workshop.category}
          </Badge>
          {workshop.offer_active ? (
            <Badge className="absolute top-2 right-2 z-10 text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground hover:bg-primary" data-testid="card-offer-badge">
              {offerLabel}
            </Badge>
          ) : isInterest ? (
            <Badge className="absolute top-2 right-2 z-10 text-[10px] px-1.5 py-0.5 gap-0.5 bg-accent text-accent-foreground hover:bg-accent" data-testid="card-interest-badge">
              <Star className="w-2.5 h-2.5 fill-current" />Tu interés
            </Badge>
          ) : spots > 0 && spots <= 3 ? (
            <Badge className="absolute top-2 right-2 z-10 text-[10px] px-1.5 py-0.5 bg-destructive text-destructive-foreground hover:bg-destructive">
              Quedan {spots}
            </Badge>
          ) : spots === 0 ? (
            <Badge className="absolute top-2 right-2 z-10 text-[10px] px-1.5 py-0.5 bg-foreground/80 text-background">Agotado</Badge>
          ) : null}
        </div>
        <div className="p-3 space-y-1.5">
          <h3 className="font-display font-bold text-sm leading-snug line-clamp-2 min-h-[2.4rem]">{workshop.name}</h3>
          <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span className="truncate">
              {workshop.business_commune ? `${workshop.business_commune} · ` : ""}{workshop.business_name}
            </span>
            {workshop.rating != null && (
              <span className="flex items-center gap-0.5 shrink-0 font-semibold text-foreground">
                <ThumbsUp className="w-3 h-3 text-emerald-600" />{workshop.rating}%
              </span>
            )}
          </div>
          {workshop.next_session?.starts_at && (
            <p className="flex items-center gap-1 text-[11px] font-medium text-primary" data-testid="workshop-start-date">
              <CalendarDays className="w-3 h-3" />Inicia el {formatDateNumeric(workshop.next_session.starts_at)}
            </p>
          )}
          <p className="flex items-center gap-1 text-[11px] font-medium text-foreground/80" data-testid={`workshop-card-mode-${workshop.id}`}>
            <Layers className="w-3 h-3 shrink-0" /><span className="truncate" title="Modalidad de selección de sesiones">{workshop.enrollment_mode === "free" ? "Sesiones sueltas" : "Grupal"}</span>
          </p>
          <div className="pt-1 flex items-end justify-between gap-2">
            {workshop.offer_active ? (
              <span className="flex items-baseline gap-1.5" data-testid="card-offer-price">
                <span className={`font-display font-extrabold text-base leading-none ${isFree ? "text-emerald-600" : "text-primary"}`}>{isFree ? "GRATIS" : CLP(workshop.effective_price_clp)}</span>
                <span className="text-[11px] text-muted-foreground line-through">{CLP(workshop.price_clp)}</span>
              </span>
            ) : (
              <span className="font-display font-extrabold text-base leading-none">{CLP(workshop.price_clp)}</span>
            )}
            {capacity > 0 && (
              <span className="flex items-center gap-1 text-[10px] text-muted-foreground shrink-0">
                <Users className="w-3 h-3" />{capacity} cupos
              </span>
            )}
          </div>
        </div>
      </Card>
    </Link>
  );
}
